import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/pages/glossary/PublicShell";
import api from "@/lib/api";
import { LayoutDashboard, BookMarked, Workflow, ShieldCheck, Users, ScrollText, LogOut, PenSquare, Mail, Magnet, Store, Ticket, HeartHandshake } from "lucide-react";

const NAV = [
  { to: "/ops", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/ops/article-studio", label: "Article Studio", icon: PenSquare, testid: "admin-nav-articles" },
  { to: "/ops/programs", label: "Programs", icon: BookMarked, testid: "admin-nav-programs" },
  { to: "/ops/marketplace", label: "Service Finder", icon: Store, testid: "admin-nav-marketplace" },
  { to: "/ops/rules", label: "Roadmap Rules", icon: Workflow, testid: "admin-nav-rules" },
  { to: "/ops/guardrails", label: "AI Guardrails", icon: ShieldCheck, testid: "admin-nav-guardrails" },
  { to: "/ops/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/ops/messages", label: "Messages", icon: Mail, testid: "admin-nav-messages" },
  { to: "/ops/leads", label: "Guide Leads", icon: Magnet, testid: "admin-nav-leads" },
  { to: "/ops/ask-anything", label: "Ask Anything", icon: HeartHandshake, testid: "admin-nav-ask" },
  { to: "/ops/coupons", label: "Coupons", icon: Ticket, testid: "admin-nav-coupons" },
  { to: "/ops/audit", label: "Audit Logs", icon: ScrollText, testid: "admin-nav-audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [newCount, setNewCount] = useState(0);
  const [aa, setAa] = useState({ open: 0, overdue: 0 });
  const doLogout = async () => { await logout(); navigate("/ops/login"); };

  useSeo({ noindex: true });

  useEffect(() => {
    const fetchCount = () => {
      api.get("/admin/messages", { params: { status: "new" } }).then((r) => setNewCount(r.data.new_count || 0)).catch(() => {});
      api.get("/admin/ask-anything/stats").then((r) => setAa({ open: r.data.open || 0, overdue: r.data.overdue || 0 })).catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-kid-navy flex text-white">
      <aside className="w-[248px] bg-[#16222f] fixed inset-y-0 left-0 flex flex-col border-r border-white/5">
        <div className="flex items-center gap-2 px-5 h-[72px]">
          <img src="/assets/kidpath-mark-light.png" alt="" className="h-8" />
          <span className="font-display text-xl font-extrabold text-white">KidPath</span>
          <span className="ml-1 text-[.6rem] uppercase tracking-widest bg-kid-teal/20 text-kid-teal px-2 py-0.5 rounded-full font-poppins">Admin</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl font-poppins text-sm transition-colors ${
                  isActive ? "bg-kid-teal text-white" : "text-white/70 hover:bg-white/5"
                }`}>
              <Icon size={18} /> <span className="flex-1">{label}</span>
              {to === "/ops/messages" && newCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-kid-coral text-white text-[.7rem] font-bold" data-testid="admin-messages-badge">
                  {newCount > 99 ? "99+" : newCount}
                </span>
              )}
              {to === "/ops/ask-anything" && aa.open > 0 && (
                <span title={aa.overdue > 0 ? `${aa.overdue} overdue` : `${aa.open} open`}
                  className={`min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full text-white text-[.7rem] font-bold ${aa.overdue > 0 ? "bg-kid-coral" : "bg-kid-teal"}`}
                  data-testid="admin-ask-badge">
                  {aa.open > 99 ? "99+" : aa.open}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user?.first_name || "Admin"}</div>
            <div className="text-xs text-white/50">{user?.role}</div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-button" className="text-white/60 hover:text-kid-coral p-2"><LogOut size={18} /></button>
        </div>
      </aside>
      <main className="flex-1 ml-[248px] bg-kid-mist text-kid-ink min-h-screen">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
